import React from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, Globe, Linkedin, Send, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { Icon: Mail,     label: "Email",     value: "hello@bsgotech.com" },
  { Icon: Phone,    label: "Phone",     value: "+49 (Berlin) · +351 (Lisboa)" },
  { Icon: Linkedin, label: "LinkedIn",  value: "linkedin.com/company/hx-ecosystems" },
  { Icon: Globe,    label: "Website",   value: "hx.bsgotech.com" },
];

const SECTORS = [
  "Hotels & Resorts",
  "Marinas & Ports",
  "Cities & Municipalities",
  "Retail & Restaurants",
  "Logistics & Fleets",
  "Energy & Utilities",
  "Investor / Fund",
  "Manufacturer",
  "Other",
];
const COUNTRIES = [
  "Germany", "Portugal", "Spain", "France", "Netherlands",
  "Belgium", "Italy", "São Tomé and Príncipe", "Other",
];

export const Contact = () => {
  const [form, setForm] = React.useState({ name: "", email: "", company: "", sector: "", country: "", message: "" });
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.company || !form.sector || !form.country) {
      toast.error("Please fill all required fields.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/strategic-contact`, form);
      setDone(true);
      setForm({ name: "", email: "", company: "", sector: "", country: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 lg:py-28">
      <div className="absolute inset-0 hx-grid-bg opacity-[0.05] pointer-events-none" />
      <div className="absolute right-0 top-1/2 w-[400px] h-[400px] -translate-y-1/2 rounded-full bg-hx-neon/[0.03] blur-[150px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16">
          {/* Left — info */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-hx-neon font-semibold">Contact</div>
            <h2 className="mt-4 font-display font-semibold text-balance text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
              Let's build the <span className="text-hx-neon">infrastructure layer</span> together.
            </h2>
            <p className="mt-5 text-[14px] text-hx-muted leading-relaxed max-w-md">
              Operators, investors, hardware vendors and municipalities — start a strategic conversation with our team.
            </p>

            <div className="mt-10 space-y-2.5 max-w-md">
              {INFO.map((it) => (
                <div key={it.label} className="rounded-lg border border-white/[0.10] bg-black/30 p-3.5 flex items-center gap-3" data-testid={`contact-info-${it.label.toLowerCase()}`}>
                  <div className="w-9 h-9 rounded-md hx-metal flex items-center justify-center shrink-0">
                    <it.Icon size={13} className="text-hx-neon" strokeWidth={1.7} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-hx-muted">{it.label}</div>
                    <div className="text-[12px] font-display text-hx-text truncate">{it.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — form */}
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-2xl border border-white/[0.10] p-6 lg:p-8"
            style={{
              background: "linear-gradient(180deg, rgba(20,26,34,0.7) 0%, rgba(10,15,26,0.90) 100%)",
              boxShadow: "0 40px 90px -50px rgba(0,0,0,0.8)",
            }}
          >
            {done ? (
              <div className="py-16 flex flex-col items-center text-center">
                <CheckCircle2 size={36} className="text-hx-neon" strokeWidth={1.6} />
                <div className="mt-5 font-display font-semibold text-xl text-hx-text">Message received</div>
                <p className="mt-2 text-[13px] text-hx-muted">Our partnerships team will reply within 48 hours.</p>
                <button onClick={() => setDone(false)} className="mt-6 text-[10px] uppercase tracking-[0.2em] text-hx-neon font-semibold">Send another →</button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="contact-input-name" />
                  <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="contact-input-email" />
                </div>
                <div className="mt-3">
                  <Field label="Company / Organisation *" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testId="contact-input-company" />
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <Select label="Sector *" value={form.sector} onChange={(v) => setForm({ ...form, sector: v })} options={SECTORS} testId="contact-input-sector" />
                  <Select label="Country *" value={form.country} onChange={(v) => setForm({ ...form, country: v })} options={COUNTRIES} testId="contact-input-country" />
                </div>
                <div className="mt-3">
                  <Field label="Message *" textarea value={form.message} onChange={(v) => setForm({ ...form, message: v })} testId="contact-input-message" />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="contact-submit"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-hx-neon text-hx-bg font-semibold text-[12px] uppercase tracking-[0.18em] hover:bg-hx-neon-soft transition-all disabled:opacity-50"
                  style={{ boxShadow: "0 0 28px -4px rgba(10,155,95,0.55)" }}
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={12} strokeWidth={2.4} />}
                  {busy ? "Sending…" : "Send message"}
                </button>
                <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-hx-muted/70 text-center">
                  Encrypted in transit · Stored privately
                </p>
              </>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, type = "text", textarea, testId }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.22em] text-hx-muted font-semibold">{label}</span>
    {textarea ? (
      <textarea
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 w-full bg-black/40 border border-white/[0.10] focus:border-hx-neon/40 rounded-lg px-3.5 py-2.5 text-[13px] text-hx-text outline-none transition-colors resize-none"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 w-full bg-black/40 border border-white/[0.10] focus:border-hx-neon/40 rounded-lg px-3.5 py-2.5 text-[13px] text-hx-text outline-none transition-colors"
      />
    )}
  </label>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.22em] text-hx-muted font-semibold">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-2 w-full bg-black/40 border border-white/[0.10] focus:border-hx-neon/40 rounded-lg px-3.5 py-2.5 text-[13px] text-hx-text outline-none transition-colors appearance-none cursor-pointer"
      style={{
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M2 4l4 4 4-4' stroke='%2393D687' stroke-width='1.5' fill='none' stroke-linecap='round'/></svg>")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 12px center",
        paddingRight: "32px",
      }}
    >
      <option value="" disabled style={{ background: "#0A0F1A" }}>Select…</option>
      {options.map((o) => <option key={o} value={o} style={{ background: "#0A0F1A" }}>{o}</option>)}
    </select>
  </label>
);
